import React from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader, Card, SkeletonRows, EmptyState } from "@/components/dash/ui";
import { AgreementStatusBadge, AgreementTypeBadge } from "@/components/dash/AgreementBadges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FileSignature, Plus, Search } from "lucide-react";

const FILTERS = ["All", "DRAFT", "IN_REVIEW", "CHANGES_REQUESTED", "APPROVED", "READY_TO_SEND", "SENT_TO_PARTNER", "FULLY_EXECUTED", "CANCELLED"];

export default function AgreementsList() {
  const nav = useNavigate();
  const [items, setItems] = React.useState(null);
  const [filter, setFilter] = React.useState("All");
  const [q, setQ] = React.useState("");

  const load = React.useCallback(() => {
    const params = {};
    if (filter !== "All") params.status = filter;
    if (q) params.q = q;
    api.get("/admin/agreements", { params }).then((r) => setItems(r.data.items || [])).catch((e) => { setItems([]); toast.error(apiError(e.response?.data?.detail)); });
  }, [filter, q]);
  React.useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageHeader title="Agreements" description="NDAs, SLAs and master agreements across the partner network." breadcrumb="VS Command Center · Agreements">
        <Button className="bg-vs-royal text-white" onClick={() => nav("/app/agreements/new")} data-testid="new-agreement-btn"><Plus className="h-4 w-4 mr-1" />New Agreement</Button>
      </PageHeader>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        <div className="flex items-center gap-1 overflow-x-auto" data-testid="agreement-filters">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${filter === f ? "bg-vs-royal text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {f === "All" ? "All" : f.replace(/_/g, " ")}</button>
          ))}
        </div>
        <div className="relative sm:ml-auto sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, partner, number…" className="pl-9" data-testid="agreement-search" />
        </div>
      </div>

      {items === null ? <SkeletonRows rows={6} /> : items.length === 0 ? (
        <EmptyState icon="FileSignature" title="No agreements yet" description="Create your first NDA, SLA or MSA for a verified partner."
          action={<Button className="bg-vs-royal text-white" onClick={() => nav("/app/agreements/new")}><Plus className="h-4 w-4 mr-1" />New Agreement</Button>} />
      ) : (
        <Card className="!p-0">
          <div className="overflow-x-auto min-w-0">
            <table className="w-full min-w-[820px] text-sm" data-testid="agreements-table">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100 bg-slate-50/50">
                <th className="px-4 py-3">Agreement</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Partner</th>
                <th className="px-4 py-3">Status</th><th className="px-4 py-3">Version</th><th className="px-4 py-3">Updated</th></tr></thead>
              <tbody>{items.map((a) => (
                <tr key={a.id} className="border-b border-slate-50 hover:bg-slate-50/60 cursor-pointer" onClick={() => nav(`/app/agreements/${a.id}`)} data-testid={`agreement-row-${a.id}`}>
                  <td className="px-4 py-3"><div className="flex items-center gap-2.5">
                    <div className="h-9 w-9 rounded-lg bg-vs-light flex items-center justify-center shrink-0"><FileSignature className="h-4 w-4 text-vs-royal" /></div>
                    <div className="min-w-0"><div className="font-medium text-slate-800 truncate">{a.title}</div><div className="text-xs text-slate-400">{a.agreement_number}</div></div>
                  </div></td>
                  <td className="px-4 py-3"><AgreementTypeBadge type={a.agreement_type} /></td>
                  <td className="px-4 py-3 text-slate-600">{a.partner_name}<div className="text-xs text-slate-400 capitalize">{a.partner_type}</div></td>
                  <td className="px-4 py-3"><AgreementStatusBadge status={a.status} /></td>
                  <td className="px-4 py-3 text-slate-500">v{a.version}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{a.updated_at ? new Date(a.updated_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
